import React, { useEffect, useMemo, useState } from "react";
import { DownloadCloud, PlayCircle, Power, RefreshCw, Save, SearchCheck, ShieldAlert } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminAutoResults() {
  const [source, setSource] = useState("https://dpbossx.net/");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [pollSeconds, setPollSeconds] = useState("120");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const matchedCount = useMemo(() => {
    const rows = Array.isArray(result?.markets) ? result.markets : [];
    return rows.filter((row) => row.matched).length;
  }, [result]);

  useEffect(() => {
    API.get("/admin/auto-results/settings")
      .then((res) => {
        setSource(res.data?.source_url || "https://dpbossx.net/");
        setEnabled(Boolean(res.data?.enabled));
        setPollSeconds(String(res.data?.poll_seconds || 120));
      })
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const res = await API.post("/admin/auto-results/settings", {
        source_url: source,
        enabled,
        poll_seconds: pollSeconds,
      });
      setEnabled(Boolean(res.data?.enabled));
      setPollSeconds(String(res.data?.poll_seconds || pollSeconds));
      setNotice("Auto result settings saved.");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const fetchDemo = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/admin/auto-results/demo", { params: { source } });
      setResult(res.data || null);
      if (res.data && res.data.success === false) {
        setError(res.data.message || "Source could not be fetched.");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to fetch demo results");
    } finally {
      setLoading(false);
    }
  };

  const runAutoNow = async () => {
    setRunning(true);
    setError("");
    setNotice("");
    try {
      const res = await API.post("/admin/auto-results/run");
      setResult(res.data || null);
      const declared = (res.data?.updates || []).filter((item) => item.declared).length;
      setNotice(declared > 0 ? `${declared} result session(s) updated.` : "Run completed. No new due result was declared.");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to run auto result");
    } finally {
      setRunning(false);
    }
  };

  const rows = Array.isArray(result?.markets) ? result.markets : [];

  return (
    <AdminLayout>
      <section style={page}>
        <div style={hero}>
          <div>
            <p style={eyebrow}>DEMO ONLY</p>
            <h1 style={title}>Auto Result Fetch Demo</h1>
            <p style={subtitle}>
              Fetches the source page, matches exact market names, and previews parsed results. It does not declare or update any result.
            </p>
          </div>
          <div style={enabled ? liveBadge : safeBadge}>
            <ShieldAlert size={18} />
            {enabled ? "Auto update ON" : "Auto update OFF"}
          </div>
        </div>

        <div style={card}>
          <div style={settingsTop}>
            <label style={toggleLabel}>
              <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
              <span style={enabled ? toggleOn : toggleOff}><Power size={14} /> {enabled ? "Auto On" : "Auto Off"}</span>
            </label>
            <label style={smallField}>
              Poll seconds
              <input style={smallInput} value={pollSeconds} onChange={(event) => setPollSeconds(event.target.value)} />
            </label>
          </div>
          <label style={label}>Source URL</label>
          <div style={sourceRow}>
            <input
              style={input}
              value={source}
              onChange={(event) => setSource(event.target.value)}
              placeholder="https://dpbossx.net/"
            />
            <button type="button" style={fetchButton} onClick={fetchDemo} disabled={loading}>
              {loading ? <RefreshCw size={18} className="spin" /> : <DownloadCloud size={18} />}
              {loading ? "Fetching" : "Fetch Demo"}
            </button>
            <button type="button" style={saveButton} onClick={saveSettings} disabled={saving}>
              {saving ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
              Save
            </button>
            <button type="button" style={runButton} onClick={runAutoNow} disabled={running}>
              {running ? <RefreshCw size={18} className="spin" /> : <PlayCircle size={18} />}
              Run Now
            </button>
          </div>
          <div style={noteBox}>
            Real auto update only declares today&apos;s due missing sessions. It never overwrites manual/existing results.
          </div>
          {error && <div style={errorBox}>{error}</div>}
          {notice && <div style={successBox}>{notice}</div>}
        </div>

        {result && (
          <div style={summaryGrid}>
            <div style={summaryCard}>
              <small>Markets checked</small>
              <strong>{rows.length}</strong>
            </div>
            <div style={summaryCard}>
              <small>Name matches</small>
              <strong>{matchedCount}</strong>
            </div>
            <div style={summaryCard}>
              <small>Source result cards</small>
              <strong>{result.source_cards || rows.filter((row) => row.result_text).length}</strong>
            </div>
            {Array.isArray(result.updates) && (
              <div style={summaryCard}>
                <small>Declared now</small>
                <strong>{result.updates.filter((item) => item.declared).length}</strong>
              </div>
            )}
          </div>
        )}

        {rows.length > 0 && (
          <div style={tableCard}>
            <div style={tableHeader}>
              <SearchCheck size={18} />
              <strong>Market Name Matching Preview</strong>
            </div>
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>Market</th>
                    <th style={th}>Match</th>
                    <th style={th}>Open</th>
                    <th style={th}>Jodi</th>
                    <th style={th}>Close</th>
                    <th style={th}>Time</th>
                    <th style={th}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.market_id}>
                      <td style={td}><strong>{row.market_name}</strong></td>
                      <td style={td}>
                        <span style={row.matched ? goodPill : badPill}>{row.matched ? "Matched" : "No match"}</span>
                      </td>
                      <td style={td}>
                        {row.open ? <span style={openPill}>{row.open}</span> : "-"}
                      </td>
                      <td style={td}>
                        {row.jodi ? <span style={jodiPill}>{row.jodi}</span> : "-"}
                      </td>
                      <td style={td}>
                        {row.close ? <span style={closePill}>{row.close}</span> : "-"}
                      </td>
                      <td style={td}>{row.timing || "-"}</td>
                      <td style={snippetTd}>
                        <strong>{row.source_name || "-"}</strong>
                        {row.result_text ? <small style={sourceResult}>{row.result_text}</small> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
      <style>{`
        .spin { animation: spin 0.9s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </AdminLayout>
  );
}

const page = { display: "grid", gap: "18px" };
const hero = { borderRadius: "24px", padding: "24px", color: "#fff", background: "linear-gradient(135deg, #0f172a, #1d4ed8 58%, #8b5cf6)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", boxShadow: "0 20px 46px rgba(37,99,235,0.22)" };
const eyebrow = { margin: "0 0 8px", color: "#bfdbfe", fontWeight: 900, letterSpacing: "0.14em", fontSize: "12px" };
const title = { margin: 0, fontSize: "clamp(26px, 4vw, 42px)", fontWeight: 900 };
const subtitle = { margin: "10px 0 0", maxWidth: "760px", color: "#dbeafe", lineHeight: 1.55 };
const safeBadge = { display: "flex", alignItems: "center", gap: "8px", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "999px", padding: "10px 14px", fontWeight: 800, whiteSpace: "nowrap" };
const liveBadge = { ...safeBadge, background: "rgba(34,197,94,0.22)", border: "1px solid rgba(187,247,208,0.55)" };
const card = { background: "#fff", borderRadius: "20px", padding: "18px", border: "1px solid #e2e8f0", boxShadow: "0 16px 36px rgba(15,23,42,0.06)" };
const settingsTop = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "14px" };
const toggleLabel = { display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 900 };
const toggleOff = { display: "inline-flex", alignItems: "center", gap: "6px", background: "#f1f5f9", color: "#475569", borderRadius: "999px", padding: "9px 12px" };
const toggleOn = { ...toggleOff, background: "#dcfce7", color: "#166534" };
const smallField = { display: "inline-grid", gap: "5px", color: "#64748b", fontSize: "12px", fontWeight: 800 };
const smallInput = { width: "110px", minHeight: "36px", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0 10px", fontWeight: 800 };
const label = { display: "block", marginBottom: "8px", fontWeight: 800, color: "#334155" };
const sourceRow = { display: "flex", flexWrap: "wrap", gap: "10px" };
const input = { flex: "1 1 320px", minWidth: 0, minHeight: "48px", border: "1px solid #cbd5e1", borderRadius: "14px", padding: "0 14px", fontSize: "15px", outline: "none" };
const fetchButton = { minHeight: "48px", border: 0, borderRadius: "14px", background: "linear-gradient(135deg, #16a34a, #22c55e)", color: "#fff", fontWeight: 900, padding: "0 18px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px", cursor: "pointer" };
const saveButton = { ...fetchButton, background: "linear-gradient(135deg, #2563eb, #3b82f6)" };
const runButton = { ...fetchButton, background: "linear-gradient(135deg, #f59e0b, #f97316)" };
const noteBox = { marginTop: "12px", background: "#f8fafc", border: "1px dashed #cbd5e1", color: "#475569", borderRadius: "14px", padding: "11px 12px", fontSize: "13px", fontWeight: 700 };
const errorBox = { marginTop: "12px", background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: "14px", padding: "12px", fontWeight: 700 };
const successBox = { ...errorBox, background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" };
const summaryCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "16px", display: "grid", gap: "6px", boxShadow: "0 12px 28px rgba(15,23,42,0.05)" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", overflow: "hidden", boxShadow: "0 16px 36px rgba(15,23,42,0.06)" };
const tableHeader = { display: "flex", alignItems: "center", gap: "10px", padding: "16px 18px", borderBottom: "1px solid #e2e8f0", color: "#0f172a" };
const tableWrap = { overflowX: "auto" };
const table = { width: "100%", borderCollapse: "collapse", minWidth: "980px" };
const th = { textAlign: "left", padding: "12px 14px", background: "#f8fafc", color: "#64748b", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.06em" };
const td = { padding: "13px 14px", borderTop: "1px solid #eef2f7", color: "#334155", verticalAlign: "top" };
const snippetTd = { ...td, maxWidth: "420px", color: "#64748b", fontSize: "13px", lineHeight: 1.45 };
const goodPill = { display: "inline-flex", padding: "6px 10px", borderRadius: "999px", background: "#dcfce7", color: "#166534", fontWeight: 900, fontSize: "12px" };
const badPill = { ...goodPill, background: "#f1f5f9", color: "#64748b" };
const openPill = { display: "inline-flex", padding: "7px 10px", borderRadius: "12px", background: "#eff6ff", color: "#1d4ed8", fontWeight: 900 };
const jodiPill = { ...openPill, background: "#fef3c7", color: "#b45309" };
const closePill = { ...openPill, background: "#fce7f3", color: "#be185d" };
const sourceResult = { display: "block", marginTop: "5px", color: "#64748b", fontWeight: 800 };

export default AdminAutoResults;
